import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ApiClient from "../../methods/api/apiClient";
import { Chart, registerables } from "chart.js";
import Layout from "../../components/global/layout";
import {
  FiFileText,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiMessageSquare,
  FiLock,
  FiFile,
  FiCreditCard,
  FiAlertCircle,
  FiDollarSign,
} from "react-icons/fi";

Chart.register(...registerables);

// ─── Constants ────────────────────────────────────────────────────────────────

const CURRENT_YEAR = new Date().getFullYear();
const AVAILABLE_YEARS = Array.from(
  { length: 5 },
  (_, i) => CURRENT_YEAR - 3 + i,
);

const CHART_TYPES = ["bar", "line", "area", "pie", "doughnut"];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatUSD = (val = 0) =>
  "$" +
  Number(val).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatCard = ({ title, value, color, bubbleImg, bg, Icon, onClick }) => (
  <div
    onClick={onClick}
    className="relative bg-white border space-y-3 border-gray-100 rounded-2xl p-5 cursor-pointer shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1 overflow-hidden"
  >
    <div className="flex items-center gap-3 justify-between">
      <div
        className="min-w-11 min-w-12 w-11 md:w-12 h-11 md:h-12 rounded-2xl flex items-center justify-center"
        style={{ background: bg }}
      >
        {Icon && (
          <Icon
            className="min-w-[20px] md:min-w-[24px] w-[20px] md:w-[24px] h-[20px] md:h-[24px]"
            color={color}
          />
        )}
      </div>
      <div className="space-y-2">
        <div className="text-lg sm:text-[22px] font-medium text-gray-800 leading-none break-all">
          {value}
        </div>
      </div>
    </div>
    <div className="text-md sm:text-lg font-medium text-gray-800">{title}</div>
    {bubbleImg && (
      <img
        src={bubbleImg}
        alt=""
        className="absolute right-0 bottom-0 w-[200px] h-[100px] opacity-30 group-hover:opacity-45 transition-opacity duration-300 object-contain pointer-events-none"
      />
    )}
  </div>
);

const SectionDivider = ({ label }) => (
  <div className="flex items-center gap-3 mb-5">
    <h2 className="text-xs font-semibold tracking-widest uppercase text-gray-600">
      {label}
    </h2>
    <div className="flex-1 h-px bg-gray-200" />
  </div>
);

const LoadingOverlay = () => (
  <div id="loader" className="loaderDiv">
    <div>
      <img
        src="/assets/img/loader.gif"
        width="auto"
        height="auto"
        alt="logo"
        className="pageLoader"
      />
    </div>
  </div>
  // <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-2xl z-10">
  //   <div className="w-6 h-6 border-2 border-[#115B9D] border-t-transparent rounded-full animate-spin" />
  // </div>
);

// ─── Chart Component ──────────────────────────────────────────────────────────

const INVOICE_COLORS = {
  paid: {
    solid: "rgba(16,185,129,0.75)",
    border: "rgba(16,185,129,1)",
    faded: "rgba(16,185,129,0.15)",
  },
  due: {
    solid: "rgba(239,68,68,0.75)",
    border: "rgba(239,68,68,1)",
    faded: "rgba(239,68,68,0.15)",
  },
};
const ESTIMATE_COLORS = {
  pending: {
    solid: "rgba(245,158,11,0.75)",
    border: "rgba(245,158,11,1)",
    faded: "rgba(245,158,11,0.15)",
  },
  accepted: {
    solid: "rgba(16,185,129,0.75)",
    border: "rgba(16,185,129,1)",
    faded: "rgba(16,185,129,0.15)",
  },
  rejected: {
    solid: "rgba(239,68,68,0.75)",
    border: "rgba(239,68,68,1)",
    faded: "rgba(239,68,68,0.15)",
  },
};

function buildDataset({ label, data, color, chartType }) {
  const isArea = chartType === "area";
  const isBar = chartType === "bar" || chartType === "stackedBar";
  return {
    label,
    data,
    backgroundColor: isArea ? color.faded : color.solid,
    borderColor: color.border,
    borderWidth: 2,
    fill: isArea,
    tension: 0.35,
    borderRadius: isBar ? 4 : 0,
  };
}

function buildChartConfig({ type, chartType, graphData }) {
  const labels = graphData?.labels || [];
  const isPieOrDoughnut = chartType === "pie" || chartType === "doughnut";

  const tooltipDollarLabel = (context) => {
    const val = context.parsed?.y ?? context.raw ?? 0;
    return `${context.dataset?.label || context.label}: ${formatUSD(val)}`;
  };

  const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 800, easing: "easeOutQuart" },
    plugins: {
      legend: {
        position: "top",
        labels: {
          boxWidth: 8,
          usePointStyle: true,
          pointStyle: "circle",
          padding: 18,
          color: "#4b5563",
          font: { weight: "600", size: 12 },
        },
      },
      tooltip: {
        backgroundColor: "#111827",
        titleColor: "#ffffff",
        bodyColor: "#f3f4f6",
        padding: 12,
        cornerRadius: 8,
        callbacks: { label: tooltipDollarLabel },
      },
    },
  };

  if (isPieOrDoughnut) {
    const [data, backgroundColor, borderColor] =
      type === "invoice"
        ? [
            [graphData?.paidAmount || 0, graphData?.dueAmount || 0],
            [INVOICE_COLORS.paid.solid, INVOICE_COLORS.due.solid],
            [INVOICE_COLORS.paid.border, INVOICE_COLORS.due.border],
          ]
        : [
            [
              graphData?.pendingAmount || 0,
              graphData?.acceptedAmount || 0,
              graphData?.rejectedAmount || 0,
            ],
            [
              ESTIMATE_COLORS.pending.solid,
              ESTIMATE_COLORS.accepted.solid,
              ESTIMATE_COLORS.rejected.solid,
            ],
            [
              ESTIMATE_COLORS.pending.border,
              ESTIMATE_COLORS.accepted.border,
              ESTIMATE_COLORS.rejected.border,
            ],
          ];

    return {
      type: chartType,
      data: {
        labels:
          type === "invoice"
            ? ["Paid Amount", "Due Amount"]
            : ["Pending Amount", "Accepted Amount", "Rejected Amount"],
        datasets: [{ data, backgroundColor, borderColor, borderWidth: 1 }],
      },
      options: { ...baseOptions, scales: {} },
    };
  }

  const scaleOptions = {
    x: {
      stacked: chartType === "stackedBar",
      grid: { display: false },
      ticks: { color: "#9ca3af", font: { size: 11 } },
    },
    y: {
      stacked: chartType === "stackedBar",
      grid: { color: "#f3f4f6" },
      ticks: {
        color: "#9ca3af",
        font: { size: 11 },
        callback: (v) => "$" + v.toLocaleString(),
      },
    },
  };

  const datasets =
    type === "invoice"
      ? [
          buildDataset({
            label: "Paid Amount ($)",
            data: graphData?.paidData || [],
            color: INVOICE_COLORS.paid,
            chartType,
          }),
          buildDataset({
            label: "Due Amount ($)",
            data: graphData?.dueData || [],
            color: INVOICE_COLORS.due,
            chartType,
          }),
        ]
      : [
          buildDataset({
            label: "Pending Amount ($)",
            data: graphData?.pendingData || [],
            color: ESTIMATE_COLORS.pending,
            chartType,
          }),
          buildDataset({
            label: "Accepted Amount ($)",
            data: graphData?.acceptedData || [],
            color: ESTIMATE_COLORS.accepted,
            chartType,
          }),
          buildDataset({
            label: "Rejected Amount ($)",
            data: graphData?.rejectedData || [],
            color: ESTIMATE_COLORS.rejected,
            chartType,
          }),
        ];

  const mappedType =
    chartType === "stackedBar"
      ? "bar"
      : chartType === "area"
        ? "line"
        : chartType;

  return {
    type: mappedType,
    data: { labels, datasets },
    options: { ...baseOptions, scales: scaleOptions },
  };
}

const InteractiveChart = ({
  title,
  type,
  graphData,
  year,
  setYear,
  icon,
  loading,
}) => {
  const canvasRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const [chartType, setChartType] = useState("bar");

  useEffect(() => {
    if (!canvasRef.current || !graphData) return;
    chartInstanceRef.current?.destroy();

    const config = buildChartConfig({ type, chartType, graphData });
    chartInstanceRef.current = new Chart(
      canvasRef.current.getContext("2d"),
      config,
    );

    return () => chartInstanceRef.current?.destroy();
  }, [graphData, chartType, type]);

  const totalCount =
    type === "invoice"
      ? graphData?.totalInvoices || 0
      : graphData?.totalQuotes || 0;
  const totalAmount = graphData?.totalAmount || 0;
  const totalTax = graphData?.totalTax || 0;

  const chartButtons = [
    ...["bar", "line", "area"].map((t) => ({ key: t, label: t })),
    ...(type === "estimate"
      ? [{ key: "stackedBar", label: "Stacked Bar" }]
      : []),
    ...["pie", "doughnut"].map((t) => ({ key: t, label: t })),
  ];

  return (
    <div className="relative bg-white rounded-3xl shadow-sm border border-gray-100 p-5 sm:p-6 flex flex-col transition-all hover:shadow-md">
      {loading && <LoadingOverlay />}

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          {/* <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center">
            {icon}
          </div> */}
          <div>
            <h4 className="text-lg xl:text-2xl font-bold text-gray-800 tracking-tight">
              {title}
            </h4>
            <p className="text-xs text-gray-600">
              Manage and track all your quote estimates
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-600">Year:</label>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="text-xs border border-gray-200 rounded-xl px-3 py-1 bg-white text-gray-600 font-bold focus:ring-0 focus:ring-[#115B9D] outline-none cursor-pointer hover:bg-gray-100 transition-colors"
          >
            {AVAILABLE_YEARS.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 mb-5">
        {[
          {
            label: "Total Count",
            value: totalCount,
            format: (v) => v,
            color: "text-[#115B9D]",
          },
          {
            label: "Total Amount",
            value: totalAmount,
            format: formatUSD,
            color: "text-[#fbbf24]",
          },
          {
            label: "Total Tax",
            value: totalTax,
            format: formatUSD,
            color: "text-[#2d8236]",
          },
        ].map(({ label, value, format, color }, i) => {
          const bgClasses = [
            "bg-[#115b9d12]",
            "bg-[#fbbf2414]",
            "bg-[#2d823617]",
          ];

          const borderColors = ["#115B9D", "#fbbf24", "#2d8236"];

          return (
            <div
              key={label}
              className={`text-center border border-l-transparent rounded-3xl space-y-1 border-l-[3px] ${
                i === 2 ? "col-span-2 lg:col-span-1" : ""
              }`}
              style={{
                background: `linear-gradient(white, white) padding-box,
                       linear-gradient(180deg, #fff, ${borderColors[i]}, #fff) border-box`,
              }}
            >
              <div
                className={`${bgClasses[i]} flex flex-col justify-center items-center w-full h-full space-y-1 rounded-[22px] px-3 py-6`}
              >
                <p className={`text-sm font-extrabold ${color}`}>
                  {format(value)}
                </p>

                <p className="text-xs sm:text-[13px] font-bold text-gray-600 uppercase tracking-wider">
                  {label}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Chart Type Toggles */}
      <div className="max-sm:w-full flex overflow-auto items-center gap-x-4 gap-y-2 mb-6 bg-white p-1.5 rounded-xl border self-end max-lg:[scrollbar-width:none] max-lg:[-ms-overflow-style:none] max-lg:[&::-webkit-scrollbar]:hidden px-3 py-2.5">
        {chartButtons.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setChartType(key)}
            className={`text-sm transition-all border-b-[3px] border-transparent whitespace-nowrap ${
              chartType === key
                ? "border-b-[2px] !border-[#115B9D] shadow-sm"
                : "text-gray-500 hover:text-gray-800"
            }`}
          >
            {label.charAt(0).toUpperCase() + label.slice(1)}
          </button>
        ))}
      </div>

      {/* Canvas */}
      <div className="relative flex-grow min-h-0 w-full bg-[#F8FAFC] border rounded-xl p-3">
        {graphData ? (
          <canvas ref={canvasRef} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
            <p className="text-sm text-gray-600 font-medium">
              No Data Available
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Icons (inline SVGs) ─────────────────────────────────────────────────────

const EstimateIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="22"
    height="22"
    viewBox="0 0 26 26"
    fill="none"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18.4167 12.1875C21.8552 12.1875 24.6458 14.9782 24.6458 18.4167C24.6458 21.8552 21.8552 24.6458 18.4167 24.6458C14.9782 24.6458 12.1875 21.8552 12.1875 18.4167C12.1875 14.9782 14.9782 12.1875 18.4167 12.1875ZM17.6042 15.7083V18.4167C17.6042 18.8652 17.9682 19.2292 18.4167 19.2292H21.125C21.5735 19.2292 21.9375 18.8652 21.9375 18.4167C21.9375 17.9682 21.5735 17.6042 21.125 17.6042H19.2292V15.7083C19.2292 15.2598 18.8652 14.8958 18.4167 14.8958C17.9682 14.8958 17.6042 15.2598 17.6042 15.7083Z"
      fill="#115B9D"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.76953 2.70817V2.1665C6.76953 1.718 7.13353 1.354 7.58203 1.354C8.03053 1.354 8.39453 1.718 8.39453 2.1665V2.70817H6.76953Z"
      fill="#115B9D"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.6035 2.70817V2.1665C17.6035 1.718 17.9675 1.354 18.416 1.354C18.8645 1.354 19.2285 1.718 19.2285 2.1665V2.70817H17.6035Z"
      fill="#115B9D"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M13.5053 23.8333H4.33268C3.54293 23.8333 2.7846 23.5191 2.2256 22.9612C1.66768 22.4022 1.35352 21.6438 1.35352 20.8541V9.20825H24.6452V14.587C23.3571 12.4973 21.0485 11.1041 18.416 11.1041C14.3806 11.1041 11.1035 14.3812 11.1035 18.4166C11.1035 20.5627 12.0308 22.4953 13.5053 23.8333ZM1.35352 7.58325V5.68742C1.35352 4.89767 1.66768 4.13934 2.2256 3.58034C2.7846 3.02242 3.54293 2.70825 4.33268 2.70825H6.77018V4.87492C6.77018 5.32342 7.13418 5.68742 7.58268 5.68742C8.03118 5.68742 8.39518 5.32342 8.39518 4.87492V2.70825H17.6035V4.87492C17.6035 5.32342 17.9675 5.68742 18.416 5.68742C18.8645 5.68742 19.2285 5.32342 19.2285 4.87492V2.70825H21.666C22.4558 2.70825 23.2141 3.02242 23.7731 3.58034C24.331 4.13934 24.6452 4.89767 24.6452 5.68742V7.58325H1.35352Z"
      fill="#115B9D"
    />
  </svg>
);

const InvoiceIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="22"
    height="22"
    viewBox="0 0 26 26"
    fill="none"
  >
    <path
      d="M21.8651 0H11.0773V7.3192C11.0773 7.80366 10.6845 8.1964 10.2001 8.1964H2.88086V24.7469C2.88086 25.4389 3.44194 26 4.13399 26H21.8651C22.5571 26 23.1182 25.4389 23.1182 24.7469V1.25313C23.1182 0.561082 22.5571 0 21.8651 0ZM16.9259 20.8083H9.78376C9.29931 20.8083 8.90656 20.4155 8.90656 19.9311C8.90656 19.4466 9.29931 19.0539 9.78376 19.0539H16.9259C17.4104 19.0539 17.8031 19.4466 17.8031 19.9311C17.8031 20.8083 17.4104 20.8083 16.9259 20.8083ZM16.9259 16.6817H9.78376C9.29931 16.6817 8.90656 16.289 8.90656 15.8045C8.90656 15.3201 9.29931 14.9274 9.78376 14.9274H16.9259C17.4104 14.9274 17.8031 15.3201 17.8031 15.8045C17.8031 16.6817 17.4104 16.6817 16.9259 16.6817ZM16.9259 12.5553H9.78376C9.29931 12.5553 8.90656 12.1626 8.90656 11.6781C8.90656 11.1937 9.29931 10.8009 9.78376 10.8009H16.9259C17.4104 10.8009 17.8031 11.1937 17.8031 11.6781C17.8031 12.5553 17.4104 12.5553 16.9259 12.5553Z"
      fill="#115B9D"
    />
    <path
      d="M9.3123 0C8.97999 0 8.66129 0.13198 8.42627 0.367098L3.24796 5.54521C3.01294 5.78023 2.88086 6.09893 2.88086 6.43124V6.44196H9.32297V0H9.3123Z"
      fill="#115B9D"
    />
  </svg>
);

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export default function Dashboard() {
  const navigate = useNavigate();

  const [countData, setCountData] = useState({});
  console.log("countData:", countData);
  const [invoiceYear, setInvoiceYear] = useState(CURRENT_YEAR);
  const [estimateYear, setEstimateYear] = useState(CURRENT_YEAR);
  const [invoiceGraphData, setInvoiceGraphData] = useState(null);
  const [estimateGraphData, setEstimateGraphData] = useState(null);

  // Per-section loading states — fixes the race-condition loader bug
  const [countLoading, setCountLoading] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [estimateLoading, setEstimateLoading] = useState(false);

  // ── Fetchers ────────────────────────────────────────────────────────────────

  const fetchCountData = useCallback(async (year) => {
    setCountLoading(true);
    try {
      const res = await ApiClient.get(
        "quickbooks-docs/admin/user-dashboard-data",
        { year },
      );
      if (res?.success) setCountData(res.data);
    } finally {
      setCountLoading(false);
    }
  }, []);

  const fetchInvoiceGraph = useCallback(async (year) => {
    setInvoiceLoading(true);
    try {
      const res = await ApiClient.get("quickbooks-docs/admin/invoice-graph", {
        year,
      });
      if (res?.success) setInvoiceGraphData(res.data);
    } finally {
      setInvoiceLoading(false);
    }
  }, []);

  const fetchEstimateGraph = useCallback(async (year) => {
    setEstimateLoading(true);
    try {
      const res = await ApiClient.get("quickbooks-docs/admin/estimate-graph", {
        year,
      });
      if (res?.success) setEstimateGraphData(res.data);
    } finally {
      setEstimateLoading(false);
    }
  }, []);

  // ── Effects ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchCountData(estimateYear);
    fetchEstimateGraph(estimateYear);
  }, [estimateYear, fetchCountData, fetchEstimateGraph]);

  useEffect(() => {
    fetchInvoiceGraph(invoiceYear);
  }, [invoiceYear, fetchInvoiceGraph]);

  // ── Derived data ─────────────────────────────────────────────────────────────

  const tabs = countData?.estimates?.tabs || {};
  const invoices = countData?.invoices || {};

  const ESTIMATE_CARDS = [
    {
      title: "Total Estimates",
      value: countData?.estimates?.total || 0,
      Icon: FiFileText,
      color: "#185FA5", // blue
      bg: "#eff5fc",
      bubbleImg: "/assets/img/bubble-1.png",
      path: "/order/management?tab=estimate",
    },
    {
      title: "Pending",
      value: tabs.pending || 0,
      Icon: FiClock,
      color: "#d97706", // amber
      bg: "#fffbeb",
      bubbleImg: "/assets/img/bubble-2.png",
      path: "/order/management?tab=estimate&subTab=pending",
    },
    {
      title: "Accepted",
      value: tabs.accepted || 0,
      Icon: FiCheckCircle,
      color: "#059669", // green
      bg: "#ecfdf5",
      bubbleImg: "/assets/img/bubble-3.png",
      path: "/order/management?tab=estimate&subTab=synced",
    },
    {
      title: "Rejected",
      value: tabs.rejected || 0,
      Icon: FiXCircle,
      color: "#dc2626", // red
      bg: "#fef2f2",
      bubbleImg: "/assets/img/bubble-4.png",
      path: "/order/management?tab=estimate&subTab=rejected",
    },
    {
      title: "Negotiation",
      value: tabs.negotiation || 0,
      Icon: FiMessageSquare,
      color: "#7c3aed", // violet
      bg: "#f5f3ff",
      bubbleImg: "/assets/img/bubble-5.png",
      path: "/order/management?tab=estimate&subTab=negotiate",
    },
    {
      title: "Closed",
      value: tabs.closed || 0,
      Icon: FiLock,
      color: "#475569", // slate
      bg: "#f1f5f9",
      bubbleImg: "/assets/img/bubble-5.png",
      path: "/order/management?tab=estimate&subTab=closed",
    },
  ];

  const INVOICE_CARDS = [
    {
      title: "Total Invoices",
      value: invoices.totalInvoices || 0,
      Icon: FiFile,
      color: "#185FA5", // blue
      bg: "#eff5fc",
      bubbleImg: "/assets/img/bubble-1.png",
      path: "/order/management?tab=invoice",
    },
    {
      title: "Paid Invoices",
      value: invoices.paidInvoices || 0,
      Icon: FiCreditCard,
      color: "#059669", // green
      bg: "#ecfdf5",
      bubbleImg: "/assets/img/bubble-3.png",
      path: "/order/management?tab=invoice",
    },
    {
      title: "Due Invoices",
      value: invoices.tabs?.due || 0,
      Icon: FiAlertCircle,
      color: "#dc2626", // red
      bg: "#fef2f2",
      bubbleImg: "/assets/img/bubble-4.png",
      path: "/order/management?tab=invoice",
    },
    {
      title: "Amount Due",
      value: formatUSD(invoices.amountDue || 0),
      Icon: FiDollarSign,
      color: "#d97706", // amber
      bg: "#fffbeb",
      bubbleImg: "/assets/img/bubble-2.png",
      path: "/order/management?tab=invoice",
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="min-h-[calc(100vh_-_75px)] md:min-h-[calc(100vh_-_95px)] mt-[15px] lg:mt-[25px]">
        <div className="max-xl:container xl:max-w-[1280px]  mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-800 tracking-tight">
              Quote & Invoice Analytics Dashboard
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Monitor, track, and analyze your QuickBooks quotes and invoices.
            </p>
          </div>

          <div className="space-y-12">
            {/* ── ESTIMATES SECTION ── */}
            <section className="relative">
              <SectionDivider label="Estimate Status" />
              <div className="grid max-[380px]:!grid-cols-1 grid-cols-2 md:grid-cols-3 gap-5">
                {ESTIMATE_CARDS.map((item) => (
                  <StatCard
                    key={item.title}
                    {...item}
                    onClick={() => navigate(item.path)}
                  />
                ))}
              </div>
            </section>

            {/* ── INVOICES SECTION ── */}
            <section className="relative">
              <SectionDivider label="Invoice Overview" />
              <div className="grid grid max-[380px]:!grid-cols-1 grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
                {INVOICE_CARDS.map((item) => (
                  <StatCard
                    key={item.title}
                    {...item}
                    onClick={() => navigate(item.path)}
                  />
                ))}
              </div>
            </section>

            <div className="h-[1px] bg-gray-200" />

            {/* ── CHARTS SECTION ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <InteractiveChart
                title="Estimate Overview"
                type="estimate"
                graphData={estimateGraphData}
                year={estimateYear}
                setYear={setEstimateYear}
                icon={<EstimateIcon />}
                loading={invoiceLoading}
              />
              <InteractiveChart
                title="Invoice Overview"
                type="invoice"
                graphData={invoiceGraphData}
                year={invoiceYear}
                setYear={setInvoiceYear}
                icon={<InvoiceIcon />}
                loading={invoiceLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
